import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useDepartment } from "../stores/departmentStore";
import { api } from "../utils/axios-instance";

const NotificationPanel = ({ darkMode }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);

  const { departments } = useDepartment();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const fetchNotifications = async () => {
    try {
      const [notificationsRes, unreadRes] = await Promise.all([
        api.get("/file/notifications/"),
        api.get("/file/notifications/unread-count/"),
      ]);

      setNotifications(notificationsRes.data || []);
      setUnreadCount(unreadRes.data?.count || 0);
    } catch (error) {
      console.log(error);
    }
  };

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/file/notifications/read/${notificationId}/`);

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );

      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.log(error);
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/file/notifications/read-all/");

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
        }))
      );

      setUnreadCount(0);
    } catch (err) {
      console.log(err);
    }
  };

  const hexToRgb = (hex) => {
    if (!hex) return null;

    const cleaned = hex.replace("#", "");

    const bigint = parseInt(cleaned, 16);

    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const createDepartmentBg = (hex, opacity = 0.08) => {
    const rgb = hexToRgb(hex);

    if (!rgb) return "#F8FAFC";

    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  };

  const getDepartmentColor = (notification) => {
    if (
      notification.type !== "file_shared" ||
      !notification.department_ids?.length
    ) {
      return null;
    }

    const dept = departments.find(
      (d) => d.id === notification.department_ids[0]
    );

    return dept?.metadata?.bg || null;
  };

  const getNotificationBackground = (notification) => {
    // FILE SHARED — always show dept color, just dimmer when read
    if (notification.type === "file_shared") {
      const color = getDepartmentColor(notification);

      if (color) {
        return notification.is_read
          ? createDepartmentBg(color, 0.2)
          : createDepartmentBg(color, 0.3);
      }
    }

    // ROLE CHANGED
    if (notification.type === "role_changed") {
      return notification.is_read ? "#F8FAFC" : "#F0FDF4";
    }

    // Dept changed
    if (notification.type === "department_changed") {
      return notification.is_read ? "#F8FAFC" : "#ECFDF5";
    }

    // Message Info
    if (notification.type === "chat_message") {
      return notification.is_read ? "#F8FAFC" : "#EEF2FF";
    }

    // DEFAULT
    return notification.is_read ? "#F8FAFC" : "#EEF2FF";
  };

  const getNotificationBorder = (notification) => {
    const color = getDepartmentColor(notification);

    if (color) {
      return color;
    }

    if (notification.type === "role_changed") {
      return "#808080";
    }

    if (notification.type === "department_changed") {
      return "#808080";
    }

    if (notification.type === "chat_message") {
      return "#6366F1";
    }

    return "#2563EB";
  };

  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return "";

    try {
      const date = timestamp.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);

      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffSecs < 60) return "Just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24)
        return `${diffHours} hr${diffHours > 1 ? "s" : ""} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

      return date.toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return "";
    }
  };

  return (
    <div
      ref={panelRef}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "34px",
        height: "34px",
      }}
    >
      {/* Bell icon with badge */}
      <Badge
        badgeContent={unreadCount}
        color="error"
        sx={{
          cursor: "pointer",
          "& .MuiBadge-badge": {
            fontSize: "10px",
            minWidth: "16px",
            height: "16px",
          },
        }}
      >
        <NotificationsNoneIcon
          onClick={handleToggle}
          className={`cursor-pointer ${darkMode ? "text-white" : "text-gray-800"}`}
          style={{
            fontSize: "18px",
            width: "18px",
            height: "18px",
            display: "block",
          }}
        />
      </Badge>

      {/* Notification panel */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: "340px",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 9999,
            overflow: "hidden",
            animation: "notif-drop-in 0.18s ease",
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
        >
          <style>{`
            @keyframes notif-drop-in {
              from { opacity: 0; transform: translateY(-8px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* ── Header ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              // justifyContent: "space-between",
              gap: "8px",
              padding: "14px 16px 10px",
              borderBottom: "1px solid #F0F1F3",
              // background: "#ffffff",
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: "15px",
                fontWeight: 700,
                // color: "#0F1117",
                color: "#1a1a2e",
                letterSpacing: "-0.01em",
              }}
            >
              Notifications
            </span>

            {unreadCount > 0 && (
              <span
                style={{
                  background: "#4f46e5",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 700,
                  borderRadius: "99px",
                  padding: "1px 7px",
                  lineHeight: "18px",
                }}
              >
                {unreadCount}
              </span>
            )}

            <button
              onClick={markAllRead}
              style={{
                background: "none",
                border: "none",
                fontSize: "11.5px",
                color: "#4f46e5",
                cursor: "pointer",
                fontFamily: "inherit",
                fontWeight: 500,
                padding: 0,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.textDecoration = "underline")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.textDecoration = "none")
              }
            >
              Mark all read
            </button>
          </div>

          {/* ── Scrollable list ── */}
          <div
            style={{
              maxHeight: "360px",
              overflowY: "auto",
              padding: "4px 0",
              scrollbarWidth: "thin",
              scrollbarColor: "#d1d5db transparent",
            }}
          >
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "32px 16px",
                  textAlign: "center",
                  color: "#9CA3AF",
                  fontSize: "13px",
                }}
              >
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={async () => {
                    await markAsRead(notification.id);
                    handleClose();
                    if (notification.type === "role_changed") {
                      navigate("/profile");
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    padding: "10px 16px",
                    borderBottom: "1px solid #F3F4F6",
                    backgroundColor: getNotificationBackground(notification),
                    borderLeft: `4px solid ${getNotificationBorder(notification)}`,
                    cursor: "pointer",
                    transition: "background 0.15s",
                    // background: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = "brightness(0.97)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = "brightness(1)";
                  }}
                >
                  {/* Unread dot */}
                  <div
                    style={{
                      width: "28px",
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      paddingTop: "3px",
                    }}
                  >
                    {!notification.is_read && (
                      <span
                        style={{
                          width: "7px",
                          height: "7px",
                          borderRadius: "50%",
                          background: "#4F46E5",
                          display: "block",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Title */}
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: notification.is_read ? 500 : 700,
                        color: "#1a1a2e",
                        lineHeight: 1.5,
                        margin: "0 0 2px 0",
                        // marginBottom: "3px",
                      }}
                    >
                      {notification.title}
                    </p>

                    {/* Message */}
                    <p
                      style={{
                        fontSize: "12.5px",
                        color: "#374151",
                        lineHeight: 1.55,
                        // marginBottom: "6px",
                        margin: "0 0 5px 0",
                        wordBreak: "break-word",
                      }}
                    >
                      {notification.message}
                    </p>

                    {/* Footer row: time + view file */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                          fontSize: "11px",
                          color: "#9CA3AF",
                        }}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {formatNotificationTime(notification.created_at)}
                      </span>

                      {notification.type === "file_shared" &&
                        notification.file_id && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              navigate(`/file/${notification.file_id}`);
                            }}
                            style={{
                              fontSize: "12px",
                              color: "#4F46E5",
                              fontWeight: 600,
                              cursor: "pointer",
                              textDecoration: "underline",
                              textUnderlineOffset: "2px",
                              flexShrink: 0,
                            }}
                          >
                            View File →
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
